(() => {
  const MODEL_TITLE_RE = /^(model|device|compatibility)\b/i;
  const BRAND_RE = /^(google|apple|samsung|motorola|moto|oppo|xiaomi|garmin|huawei|oneplus|sony|nokia|honor|vivo|realme)\s+/i;
  const GROUP_THRESHOLD = 8;
  const MAX_GROUP_TABS = 8;
  const ROOT_SELECTOR = [
    '.iphone17-product-pilot variant-radios',
    ':is(.page-iphone17-pilot, .page-device-series) sticky-addtocart variant-radios'
  ].join(', ');

  const titleCase = (value) => String(value || '').replace(/\b\w/g, (char) => char.toUpperCase());

  const optionTitle = (fieldset) => {
    const title = fieldset.closest('.prd-option')?.querySelector('.prd-option-title');
    if (!title) return '';
    const firstText = [...title.childNodes].find((node) => node.nodeType === Node.TEXT_NODE);
    return (firstText?.textContent || title.textContent || '').replace(/:$/, '').trim();
  };

  const isModelFieldset = (fieldset) => {
    if (!fieldset || fieldset.classList.contains('has-option-color')) return false;
    if (MODEL_TITLE_RE.test(optionTitle(fieldset))) return true;
    return fieldset.classList.contains('option-first') && fieldset.matches('[data-lvl="0"]');
  };

  const parseModelKey = (name) => {
    const raw = String(name || '').toLowerCase();
    const mm = raw.match(/(\d+(?:\.\d+)?)\s*mm\b/);
    const size = mm ? Number(mm[1]) : 0;
    const withoutSize = raw.replace(/(\d+(?:\.\d+)?)\s*mm\b/g, ' ');
    const nums = [...withoutSize.matchAll(/(\d+(?:\.\d+)?)/g)].map((match) => Number(match[1]));
    const gens = nums.filter((num) => num < 1900 || num > 2100);
    const gen = (gens.length ? gens : nums).reduce((max, num) => Math.max(max, num), 0);

    let edition = 0;
    if (/\bultra\b/.test(raw)) edition = 50;
    else if (/\bpro\s*max\b/.test(raw)) edition = 40;
    else if (/\bmax\b/.test(raw)) edition = 30;
    else if (/\bpro\s*plus\b/.test(raw)) edition = 25;
    else if (/\bpro\b/.test(raw)) edition = 20;
    else if (/\bplus\b|\+$/.test(raw)) edition = 10;

    return { gen, edition, size, raw };
  };

  const compareModels = (left, right) => {
    const a = parseModelKey(left);
    const b = parseModelKey(right);
    if (b.gen !== a.gen) return b.gen - a.gen;
    if (b.edition !== a.edition) return b.edition - a.edition;
    if (b.size !== a.size) return b.size - a.size;
    return a.raw.localeCompare(b.raw);
  };

  const parseModelMeta = (name) => {
    const cleaned = String(name || '').replace(/^for\s+/i, '').replace(/\s+/g, ' ').trim();
    const core = cleaned.toLowerCase()
      .replace(/(\d+(?:\.\d+)?)\s*(mm|")\b/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    const patterns = [
      { re: /\b(fenix|epix)\s+(\d+|e)\s*(s|x)?\b/i, family: (match) => titleCase(match[1]), gen: (match) => match[2].toUpperCase(), line: (match) => (match[3] || '').toUpperCase() },
      { re: /\b(forerunner|venu|instinct|vivoactive)\s+(\d+)\s*(s)?\b/i, family: (match) => titleCase(match[1]), gen: (match) => match[2], line: (match) => (match[3] || '').toUpperCase() },
      { re: /\bgalaxy\s+watch\s*(ultra)?\s*(\d+)?/i, family: () => 'Galaxy Watch', gen: (match) => (match[1] ? 'Ultra' : (match[2] || 'Watch')), line: () => '' },
      { re: /\bpixel\s+watch\s*(\d+)?/i, family: () => 'Pixel Watch', gen: (match) => match[1] || '1', line: () => '' },
      { re: /\biphone\s+(se|xs|xr|x|\d+)/i, family: () => 'iPhone', gen: (match) => match[1].toUpperCase(), line: () => '' },
      { re: /\bpixel\s+(\d+)/i, family: () => 'Pixel', gen: (match) => match[1], line: () => '' },
      { re: /\bgalaxy\s+z\s+(fold|flip)\s*(\d+)/i, family: (match) => `Z ${titleCase(match[1])}`, gen: (match) => match[2], line: () => '' },
      { re: /\bgalaxy\s+(s|a)\s*(\d+)/i, family: (match) => `Galaxy ${match[1].toUpperCase()}`, gen: (match) => match[2], line: () => '' },
      { re: /\b(?:oppo\s+)?(reno|find)\s*(\d+)/i, family: (match) => titleCase(match[1]), gen: (match) => match[2], line: () => '' },
      { re: /\b(?:oppo\s+)?a\s*(\d+)/i, family: () => 'OPPO A', gen: (match) => match[1], line: () => '' },
      { re: /\b(?:moto(?:rola)?\s+)?(edge|razr)\s*(\d+)/i, family: (match) => titleCase(match[1]), gen: (match) => match[2], line: () => '' },
      { re: /\b(?:moto(?:rola)?\s+)?g\s*(\d+)/i, family: () => 'Moto G', gen: (match) => match[1], line: () => '' }
    ];

    for (const pattern of patterns) {
      const match = core.match(pattern.re);
      if (!match) continue;
      const family = pattern.family(match);
      const gen = String(pattern.gen(match) || '').toUpperCase();
      const line = String(pattern.line(match) || '').toUpperCase();
      let groupSort = Number(gen);
      if (gen === 'E' || gen === 'SE') groupSort = 0.5;
      else if (gen === 'X' || gen === 'XS' || gen === 'XR') groupSort = 10;
      else if (gen === 'ULTRA') groupSort = 100;
      else if (Number.isNaN(groupSort)) groupSort = 0;

      return {
        groupKey: `${family}-${gen}`.toLowerCase(),
        groupLabel: `${family} ${gen}`.trim(),
        groupSort,
        lineKey: line ? `${gen}${line}` : gen,
        lineLabel: line ? `${gen}${line}` : gen,
        lineSort: line === 'X' ? 3 : line === 'S' ? 1 : 2
      };
    }

    return {
      groupKey: 'other',
      groupLabel: 'Other',
      groupSort: -1,
      lineKey: 'other',
      lineLabel: 'Other',
      lineSort: 0
    };
  };

  const coarsenMetas = (metas) => {
    const keys = new Set(metas.map((meta) => meta.groupKey));
    if (keys.size <= MAX_GROUP_TABS) return metas;

    return metas.map((meta) => {
      const gen = String(meta.groupLabel.split(' ').pop() || '');
      if (!/^\d{2,}$/.test(gen)) return meta;
      const bucket = gen[0];
      const family = meta.groupLabel.replace(new RegExp(`\\s+${gen}$`), '');
      return {
        ...meta,
        groupKey: `${family}-bucket-${bucket}`.toLowerCase(),
        groupLabel: `${family} ${bucket}x`,
        groupSort: Number(bucket) * 10
      };
    });
  };

  const uniqueSorted = (items, keyName, labelName, sortName) => {
    const map = new Map();
    items.forEach((item) => {
      if (!map.has(item[keyName])) {
        map.set(item[keyName], {
          key: item[keyName],
          label: item[labelName],
          sort: item[sortName]
        });
      }
    });
    return [...map.values()].sort((left, right) => right.sort - left.sort || left.label.localeCompare(right.label));
  };

  const shortenLabels = (labels) => {
    const values = labels.map((label) => label.querySelector('input')?.value || '');
    if (!values.length) return;

    const normalized = values.map((value) => value.replace(/^for\s+/i, ''));
    const firstBrand = normalized[0].match(BRAND_RE);
    const canStripBrand = Boolean(firstBrand) && normalized.every((value) => {
      if (!new RegExp(`^${firstBrand[1]}\\s+`, 'i').test(value)) return false;
      return value.replace(BRAND_RE, '').trim().length > 0;
    });

    labels.forEach((label, index) => {
      const value = values[index];
      let display = normalized[index];
      if (canStripBrand) display = display.replace(BRAND_RE, '');
      display = display.replace(/\s+/g, ' ').trim() || value;

      const textEl = label.querySelector('.prd-option-label-text');
      if (textEl) textEl.textContent = display;
      else {
        const node = [...label.childNodes].find((child) => child.nodeType === Node.TEXT_NODE && child.textContent.trim());
        if (node) node.textContent = ` ${display} `;
      }

      if (!label.getAttribute('title')) label.setAttribute('title', value);
    });
  };

  const modelValue = (label) => label.querySelector('input')?.value || '';

  const visibleModelLabels = (fieldset) => Array.from(fieldset.querySelectorAll(':scope > label.label-radio'))
    .filter((label) => !label.classList.contains('icase-model-hidden'));

  const applyModelFilter = (fieldset, { selectFallback = false } = {}) => {
    const state = fieldset._icaseSwitcher;
    if (!state) return;

    const labels = Array.from(fieldset.querySelectorAll(':scope > label.label-radio'));
    labels.forEach((label) => {
      const meta = label._icaseMeta;
      const matchGroup = meta.groupKey === state.groupKey;
      const matchLine = !state.lineKey || meta.lineKey === state.lineKey;
      label.classList.toggle('icase-model-hidden', !(matchGroup && matchLine));
    });

    state.groupButtons.forEach((button) => {
      button.setAttribute('aria-pressed', button.dataset.key === state.groupKey ? 'true' : 'false');
    });
    state.lineButtons.forEach((button) => {
      button.setAttribute('aria-pressed', button.dataset.key === state.lineKey ? 'true' : 'false');
    });

    const groupLines = uniqueSorted(
      labels.map((label) => label._icaseMeta).filter((meta) => meta.groupKey === state.groupKey),
      'lineKey',
      'lineLabel',
      'lineSort'
    );
    const showLines = groupLines.length > 1 && labels.filter((label) => label._icaseMeta.groupKey === state.groupKey).length > GROUP_THRESHOLD;
    state.lineRow.hidden = !showLines;
    state.lineButtons.forEach((button) => {
      button.hidden = !showLines || !groupLines.some((line) => line.key === button.dataset.key);
    });

    if (!selectFallback) return;

    const checked = labels.find((label) => label.querySelector('input')?.checked);
    if (checked && !checked.classList.contains('icase-model-hidden') && !checked.classList.contains('disabled')) return;

    const fallback = visibleModelLabels(fieldset).find((label) => !label.classList.contains('disabled'));
    if (fallback) fallback.click();
  };

  const syncSwitcherToSelection = (fieldset) => {
    const state = fieldset._icaseSwitcher;
    if (!state) return;
    const checked = Array.from(fieldset.querySelectorAll(':scope > label.label-radio')).find((label) => label.querySelector('input')?.checked);
    const meta = checked?._icaseMeta;
    if (!meta) return;
    if (state.groupKey === meta.groupKey && (!state.lineKey || state.lineKey === meta.lineKey)) {
      applyModelFilter(fieldset);
      return;
    }
    state.groupKey = meta.groupKey;
    state.lineKey = meta.lineKey;
    applyModelFilter(fieldset);
  };

  const buildModelSwitcher = (fieldset, labels) => {
    if (labels.length <= GROUP_THRESHOLD) return;

    const metas = coarsenMetas(labels.map((label) => parseModelMeta(modelValue(label))));
    labels.forEach((label, index) => {
      label._icaseMeta = metas[index];
    });

    const groups = uniqueSorted(metas, 'groupKey', 'groupLabel', 'groupSort');
    if (groups.length < 2 || groups.length > MAX_GROUP_TABS) return;

    const largestGroup = Math.max(...groups.map((group) => metas.filter((meta) => meta.groupKey === group.key).length));
    if (largestGroup >= labels.length) return;

    const wrap = fieldset.closest('.td_d') || fieldset.parentElement;
    if (!wrap) return;

    const existing = wrap.querySelector(':scope > .icase-model-switcher');
    if (existing) existing.remove();

    const switcher = document.createElement('div');
    switcher.className = 'icase-model-switcher';
    const groupRow = document.createElement('div');
    groupRow.className = 'icase-model-switcher__row';
    groupRow.setAttribute('role', 'tablist');
    groupRow.setAttribute('aria-label', 'Model series');
    const lineRow = document.createElement('div');
    lineRow.className = 'icase-model-switcher__row icase-model-switcher__row--lines';
    lineRow.setAttribute('role', 'tablist');
    lineRow.setAttribute('aria-label', 'Model size');

    const groupButtons = groups.map((group) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'icase-model-switcher__tab';
      button.dataset.key = group.key;
      button.textContent = group.label;
      groupRow.appendChild(button);
      return button;
    });

    const allLines = uniqueSorted(metas, 'lineKey', 'lineLabel', 'lineSort');
    const lineButtons = allLines.map((line) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'icase-model-switcher__tab icase-model-switcher__tab--line';
      button.dataset.key = line.key;
      button.textContent = line.label;
      lineRow.appendChild(button);
      return button;
    });

    switcher.append(groupRow, lineRow);
    wrap.insertBefore(switcher, fieldset);

    const checked = labels.find((label) => label.querySelector('input')?.checked) || labels[0];
    const selectedMeta = checked?._icaseMeta || metas[0];
    const state = {
      groupKey: selectedMeta.groupKey,
      lineKey: selectedMeta.lineKey,
      groupButtons,
      lineButtons,
      lineRow
    };
    fieldset._icaseSwitcher = state;

    groupButtons.forEach((button) => {
      button.addEventListener('click', () => {
        if (state.groupKey === button.dataset.key) return;
        state.groupKey = button.dataset.key;
        const current = labels.find((label) => label.querySelector('input')?.checked);
        const groupMetas = labels.map((label) => label._icaseMeta).filter((meta) => meta.groupKey === state.groupKey);
        const lines = uniqueSorted(groupMetas, 'lineKey', 'lineLabel', 'lineSort');
        const useLines = lines.length > 1 && groupMetas.length > GROUP_THRESHOLD;
        const selectedStillHere = current?._icaseMeta?.groupKey === state.groupKey;
        state.lineKey = useLines
          ? (selectedStillHere ? current._icaseMeta.lineKey : lines[0].key)
          : '';
        applyModelFilter(fieldset, { selectFallback: true });
      });
    });

    lineButtons.forEach((button) => {
      button.addEventListener('click', () => {
        if (state.lineKey === button.dataset.key) return;
        state.lineKey = button.dataset.key;
        applyModelFilter(fieldset, { selectFallback: true });
      });
    });

    applyModelFilter(fieldset);
  };

  const enhanceFieldset = (fieldset) => {
    if (!isModelFieldset(fieldset) || fieldset.dataset.icaseModelReady === 'true') return;

    const labels = Array.from(fieldset.querySelectorAll(':scope > label.label-radio'));
    if (labels.length > 1) {
      labels
        .sort((left, right) => compareModels(modelValue(left), modelValue(right)))
        .forEach((label) => fieldset.appendChild(label));
      shortenLabels(labels);
      buildModelSwitcher(fieldset, labels);
    }

    fieldset.dataset.icaseModelReady = 'true';
  };

  const syncSelection = (radios) => {
    radios.querySelectorAll('fieldset.prd-option-list').forEach((fieldset) => {
      const labels = Array.from(fieldset.querySelectorAll(':scope > label.label-radio'));
      const checked = labels.find((label) => label.querySelector('input')?.checked);
      if (checked?.classList.contains('disabled')) {
        const fallback = labels.find((label) => !label.classList.contains('disabled') && !label.classList.contains('icase-model-hidden'));
        if (fallback) {
          fallback.click();
          return;
        }
      }

      labels.forEach((label) => {
        const selected = Boolean(label.querySelector('input')?.checked) && !label.classList.contains('disabled');
        label.classList.toggle('swatch-active', selected);
      });

      syncSwitcherToSelection(fieldset);
    });
  };

  const enhance = (root = document) => {
    root.querySelectorAll(ROOT_SELECTOR).forEach((radios) => {
      radios.querySelectorAll('fieldset.prd-option-list').forEach(enhanceFieldset);
      syncSelection(radios);
      if (!radios.dataset.icaseOptionsBound) {
        radios.addEventListener('change', () => {
          window.requestAnimationFrame(() => syncSelection(radios));
        });
        radios.dataset.icaseOptionsBound = 'true';
      }
      radios.dataset.icaseOptionsReady = 'true';
    });
  };

  const start = () => {
    enhance();
    document.addEventListener('shopify:section:load', (event) => enhance(event.target || document));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
